import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, firstValueFrom, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { AccountScope } from 'app/core/account-scope';
import { IdentityService } from 'app/core/identity/identity.service';
import { UserService } from 'app/user/user.service';

// ── Section interfaces ─────────────────────────────────────────────────────
// Preferences that follow the user across accounts. Per-account ones live in UiPreferencesService.

export interface SideMenuPreferences {
  /** URLs of "new" nav items whose notification dot the user has already seen. */
  seenNewItems?: string[];
}

export interface AccountScopePreferences {
  bySite?: { [siteId: number]: AccountScope };
}

export interface OnsiteExperiencesPreferences {
  statusFilter?: string;
}

export interface UserUiPreferences {
  sideMenu?: SideMenuPreferences;
  accountScope?: AccountScopePreferences;
  onsiteExperiences?: OnsiteExperiencesPreferences;
}

// ── Service ────────────────────────────────────────────────────────────────

const SAVE_DEBOUNCE_MS = 1500;
/** Right after sign-in or sign-up the request can be refused until the site context settles; nothing else retries it. */
const LOAD_RETRY_DELAYS_MS = [1000, 3000, 10000];

@Injectable({
  providedIn: 'root',
})
export class UserUiPreferencesService {
  private readonly userService = inject(UserService);
  private readonly identityService = inject(IdentityService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly state = signal<UserUiPreferences>({});
  private readonly loadedSignal = signal(false);
  /** False until the signed-in user's preferences have arrived; reads before that see empty defaults. */
  readonly loaded = this.loadedSignal.asReadonly();

  private loading: Promise<void> | null = null;
  /** Bumped when the user changes, so a load still in flight for the previous user is ignored. */
  private session = 0;
  /** Whose preferences the state holds. */
  private userEmail = this.identityService.currentIdentity?.email;
  private loadRetries = 0;
  private loadRetryTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingSave = false;
  private readonly saveRequest$ = new Subject<void>();
  private readonly reset$ = new Subject<void>();

  constructor() {
    this.saveRequest$
      .pipe(
        // A save still waiting when the user signs out is dropped, so it can't land on the next user.
        switchMap(() => timer(SAVE_DEBOUNCE_MS).pipe(takeUntil(this.reset$))),
        // A failed save is skipped rather than ending the stream, which would stop every later save.
        switchMap(() =>
          this.userService.updateUiPreferences(JSON.stringify(this.state())).pipe(catchError(() => EMPTY)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => (this.pendingSave = false));

    // The app isn't reloaded between users, so drop the previous user's state and fetch the next one's.
    // Keyed on the email rather than onSignout: a new user can sign in without that event firing first.
    this.identityService.onIdentityChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((identity) => this.onIdentityChanged(identity?.email));

    window.addEventListener('pagehide', () => this.flushPendingSave());
  }

  private flushPendingSave(): void {
    if (!this.pendingSave) {
      return;
    }
    this.pendingSave = false;
    this.userService.updateUiPreferencesOnUnload(JSON.stringify(this.state()));
  }

  private reset(): void {
    this.session++;
    clearTimeout(this.loadRetryTimer);
    this.loadRetries = 0;
    this.reset$.next();
    this.pendingSave = false;
    this.loading = null;
    this.state.set({});
    this.loadedSignal.set(false);
  }

  private onIdentityChanged(email: string | undefined): void {
    if (email === this.userEmail) {
      return;
    }
    this.reset();
    this.userEmail = email;
    if (email) {
      void this.load();
    }
  }

  load(): Promise<void> {
    this.loading ??= this.fetch();
    return this.loading;
  }

  private async fetch(): Promise<void> {
    const session = this.session;
    let preferences: UserUiPreferences = {};
    try {
      const raw = await firstValueFrom(this.userService.getUiPreferences());
      if (raw) {
        preferences = JSON.parse(raw);
      }
    } catch {
      // Not "nothing stored": treating it as empty would show every dot and let the next save wipe the real list.
      // Stay unloaded and try again, e.g. right after sign-in before the site context has settled.
      if (this.session === session) {
        this.loading = null;
        this.scheduleLoadRetry();
      }
      return;
    }
    // A sign-out while the request was in flight makes this answer stale.
    if (this.session !== session) {
      return;
    }
    clearTimeout(this.loadRetryTimer);
    this.loadRetries = 0;
    this.state.set(preferences);
    this.loadedSignal.set(true);
  }

  private scheduleLoadRetry(): void {
    const delay = LOAD_RETRY_DELAYS_MS[this.loadRetries];
    if (delay === undefined) {
      return;
    }
    this.loadRetries++;
    clearTimeout(this.loadRetryTimer);
    this.loadRetryTimer = setTimeout(() => void this.load(), delay);
  }

  get<K extends keyof UserUiPreferences>(section: K): UserUiPreferences[K] {
    return this.state()[section];
  }

  update<K extends keyof UserUiPreferences>(section: K, value: UserUiPreferences[K]): void {
    this.state.update((current) => ({ ...current, [section]: value }));
    this.pendingSave = true;
    this.saveRequest$.next();
  }
}
